<template>
  <el-dialog
    width="517px"
    custom-class="guide-map-popup-wrapper"
    :visible="visible"
    :append-to-body="true"
    :close-on-click-modal="false"
    :close-on-press-escape="false"
    :show-close="false"
  >
    <div v-loading="pageLoading" class="guide-map-popup-container">
      <img src="@/assets/img/share-icon/map-fuel-plan.svg" alt="" class="ic-logo" />
      <div class="skip" @click="handleClose">Skip</div>
      <div class="guide-map-popup-title">
        Generate an Optimized Fuel Plan for Your Trip to Save Money on Fuel
      </div> 
      <div class="waypoint-list">
        <div class="waypoint-list-title">Create a trip to see how much you can save!</div>
        <div id="itemList" class="item-list">
          <div v-for="(waypoint, index) in waypointList" :key="index" class="item">
            <div class="waypoint-genre">
              <svg-icon v-if="waypoint.stopPointType === 'origin'" name="ic-origin" class="ic-waypoint" />
              <svg-icon v-else-if="waypoint.stopPointType === 'target'" name="ic-destination" class="ic-waypoint" />
              <span v-else class="waypoint-index">{{ index }}</span>
              <div v-if="waypoint.stopPointType !== 'target'" class="dividing">
                <i v-for="point in 4" :key="point" class="point"></i>
              </div>
            </div>
            <Input
              :index="index"
              :info="waypoint"
              :placeholder="index === 0 ? 'Enter starting point' : 'Enter destination point'"
              class="input-container"
              @change="handleChange"
            />
          </div>
        </div>
        <div class="add-waypoint" @click="handleAdd">
          <svg-icon name="addIcon" class="add-icon" />
          Add destination
        </div>
      </div>
      <div class="footer">
        <button
          :disabled="loading || disabled"
          :class="{ 'is-loading': loading }"
          @click="handleSubmit"
        >
          <img v-if="loading" src="@/assets/img/share-icon/guide-button-loading.png" alt="" class="ic-loading" />
          <span class="text">
            <template v-if="loading">Generating Fuel Plan...</template>
            <template v-else>View Fuel Plan Details</template>
          </span>
        </button>
      </div>
    </div>
  </el-dialog>
</template>
<script>
import { mapActions, mapState, mapMutations } from 'vuex'
import axios from 'axios'
import cloneDeep from 'lodash/cloneDeep'
import { tripPlanPointInfo } from '@/store/modules/trip-plan'
import tripPlanService from '@/store/modules/trip-plan/service'
import Input from '@/views/map/components/trip-plan-v2/components/waypoint/guide-input.vue'
import { fuelPlanSettings, calculateRoute, calcFuelPlan } from './service'
import { convertMetersToMiles } from '@/filters'
import { onboardingCompleteStatus } from '@/api/onboarding'
import { dispatchCommandLike$ } from "@/shared-services";

export default {
  components: { Input },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    close: {
      type: Function,
      default: () => {}
    },
    success: {
      type: Function,
      default: () => {}
    }
  },
  data() {
    return {
      waypointList: [],
      activeTruck: null,
      settingList: ['harshTurn', 'unpaved'],
      Service: new tripPlanService(),
      loading: false,
      pageLoading: false,
      routeList: [],
      fuelResult: null,
      cancelToken: axios.CancelToken,
      routeSource: null,
      fuelSource: null
    }
  },
  watch: {
    visible: {
      handler(status) {
        if (status) {
          this.$buriedPoints(26.1)
          this.initTruckList()
          this.initWaypointList()
        }
      },
      immediate: true
    },
    waypointList: {
      handler() {
        this.removeSource()
        this.routeList = []
        this.fuelResult = null
        if (this.disabled) {
          this.loading = false
        } else {
          this.handleRequest()
        }
      },
      deep: true
    }
  },
  created() {
    this.initWaypointList()
  },
  computed: {
    ...mapState({
      userId: state => state.user.userId,
      fuelSaveStatus: state => state.user.fuelSaveStatus
    }),
    disabled() {
      return [this.waypointList[0], this.waypointList[this.waypointList.length - 1]].some(item => !item.value)
    },
    resultInfo() {
      try {
        let fuelPlanResult = this.fuelResult.fuelPlanResult
        if (
          this.fuelResult.routeType === 'fuelSuccessful'
          && fuelPlanResult.code === 200
          && fuelPlanResult.total_fuel_volume !== 0
        ) {
          return {
            type: 'getNormalResults'
          }
        }
      } catch(error) {
        console.log(error)
      }

      try {
        let fuelPlanResult = this.fuelResult.fuelPlanResult
        if (
          this.fuelResult.routeType === 'fuelSuccessful'
          && fuelPlanResult.code === 200
          && fuelPlanResult.total_fuel_volume === 0
        ) {
          return {
            type: 'getNoNeedToFuelResults'
          }
        }
      } catch(error) {
        console.log(error)
      }
      return { type: 'getEmptyResults' }
    }
  },
  methods: {
    ...mapMutations({
      SET_SETTING_LIST: 'tripPlan/SET_SETTING_LIST',
      SET_TRUCK_INFO: 'tripPlan/SET_TRUCK_INFO'
    }),
    ...mapActions({
      getTruckListAsync: 'truck/getTruckList',
      updateTripPlanPointsAsync: 'tripPlan/updateTripPlanPoints',
      updateTripPlanInfoAsync: 'tripPlan/updateTripPlanInfo',
      updateCalculateRouteResultAsync: 'tripPlan/updateCalculateRouteResult',
      updateShowSecondarySiderbarAsync: 'siderbar/updateShowSecondarySiderbar',
      updateFuelPlanSettingsAsync: 'tripPlan/updateFuelPlanSettings',
      pushLeftDrawerAsync: 'siderbar/pushLeftDrawer',
      calcAllFuelPlanAsync: 'tripPlan/calcAllFuelPlan',
      getUser: 'user/getUser'
    }),
    initTruckList() {
      this.pageLoading = true
      this.getTruckListAsync().then(list => {
        this.activeTruck = list[0]
        this.pageLoading = false
      })
    },
    initWaypointList() {
      this.waypointList = this.Service.addWaypoint([cloneDeep(tripPlanPointInfo), cloneDeep(tripPlanPointInfo)])
    },
    handleAdd() {
      this.waypointList = this.Service.addWaypoint([...this.waypointList, cloneDeep(tripPlanPointInfo)])
      this.$nextTick(() => {
        let listDom = document.querySelector('#itemList')
        listDom.scrollTop = listDom.scrollHeight
      })
    },
    handleChange({ info, index }) {
      this.waypointList = this.waypointList.map((item, i) => {
        if (i === index) {
          return info
        } else {
          return item
        }
      })
    },
    handleClose() {
      this.handleOpenMission()
      this.removeSource()
      this.close()
    },
    initMapParams() {
      let tripPlanPoints = this.waypointList.filter(item => item.value)
      this.SET_SETTING_LIST(this.settingList)
      this.SET_TRUCK_INFO(this.activeTruck)
      this.updateTripPlanInfoAsync({
        settingList: this.settingList,
        truckInfo: this.activeTruck,
        tripPlanPoints: tripPlanPoints,
        routes: this.routeList
      })
      this.updateTripPlanPointsAsync({ type: 'backfill', reset: true, list: tripPlanPoints })
      this.updateCalculateRouteResultAsync({ type: 'backfill', list: this.routeList })
      this.updateShowSecondarySiderbarAsync(false)
      if (this.fuelResult) {
        this.updateFuelPlanSettingsAsync({ settings: fuelPlanSettings, isSave: true })
      } else {
        this.calcAllFuelPlanAsync()
      }
    },
    JumpFuelPlan() {
      this.removeSource()
      this.initMapParams()
      this.$buriedPoints(26.3, {
        'Number of Stops': this.waypointList.filter(item => item.value).length,
        'Distance of Route': this.getRouteDistance(this.routeList[0])
      })
      this.pushLeftDrawerAsync({
        type: 'route-detail',
        actions: 'tripPlan/updateShowRouteDetailStatus',
        root: true,
        width: 440,
        params: { tab: 'fuelPlan' }
      })
      setTimeout(() => {
        this.success()
        this.$router.push('/map/index')
      }, 0)
    },
    removeSource() {
      if (this.routeSource) {
        this.routeSource.cancel('cancel request')
        this.routeSource = null
      }
      if (this.fuelSource) {
        this.fuelSource.cancel('cancel request')
        this.fuelSource = null
      }
    },
    handleOpenMission() {
       dispatchCommandLike$.subscribe((x) => x.openMissionPanel())
    },
    handleSubmit() {
      this.handleOpenMission()
      if(!this.fuelSaveStatus){
        onboardingCompleteStatus({fuelSaveStatus: true}).then(() => {
          this.getUser();
        })
      }
      if (this.routeList.length) {
        this.JumpFuelPlan()
      } else {
        this.loading = true
      }
    },
    async handleRequest() {
      let routeList = []
      try {
        let tripPlanPoints = this.waypointList.filter(item => item.value)
        this.routeSource = this.cancelToken.source()
        routeList = await calculateRoute(this.settingList, this.activeTruck, tripPlanPoints, this.userId, this.routeSource.token)
        this.routeList = routeList
        if (this.loading) {
          this.JumpFuelPlan()
          return null
        }
      } catch {
        this.routeList = []
      }
      this.routeSource = null
      try {
        this.fuelSource = this.cancelToken.source()
        let fuelResult = await calcFuelPlan(routeList[0], this.fuelSource.token)
        routeList[0] = Object.assign(routeList[0], fuelResult)
        this.routeList = routeList
        this.fuelResult = fuelResult
        this.submitBuriedPoint()
      } catch {
        this.fuelResult = null
      }
      this.fuelSource = null
    },
    getOutcome(type) {
      switch(type) {
        case 'getNormalResults':
          return 'Get Results'
        case 'getNoNeedToFuelResults':
          return 'No Need to Fuel'
        default:
          return 'Error'
      }
    },
    getRouteDistance(route) {
      return convertMetersToMiles(route.summary.distance, 3)
    },
    submitBuriedPoint() {
      setTimeout(() => {
        let params = {
          Outcome: this.getOutcome(this.resultInfo.type),
          'Number of Stops': this.waypointList.filter(item => item.value).length,
          'Distance of Route': this.getRouteDistance(this.routeList[0])
        }
        this.$buriedPoints(26.2, params)
      }, 0)
    }
  }
}
</script>
<style lang="scss">
  .guide-map-popup-wrapper {
    display: flex;
    flex-direction: column;
    margin: 0 !important;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translateX(-50%) translateY(-50%);
    max-height: calc(100% - 30px);
    max-width: calc(100% - 30px);
    border-radius: 8px !important;
    .el-dialog__header, .el-dialog__footer {
      display: none;
    }
    .el-dialog__body {
      padding: 0 !important;
      word-break: normal !important;
      flex: 1;
    }
  }
</style>
<style lang="scss" scoped>
  .guide-map-popup-container {
    padding: 44px 24px 24px;
    position: relative;
    background: #FFFFFF;
    width: 517px;
    background-image: url('~@/assets/img/share-icon/popup-bg.svg');
    background-size: cover;
    background-repeat: no-repeat;
    border-radius: 8px;
    .ic-logo {
      position: absolute;
      top: -24px;
      left: 50%;
      transform: translateX(-50%);
      width: 97px;
      height: 64px;
    }
    .skip {
      position: absolute;
      top: 14px;
      right: 16px;
      user-select: none;
      font-family: 'Open Sans';
      font-style: normal;
      font-weight: 500;
      font-size: 12px;
      line-height: 16px;
      color: #A0B4D6;
      cursor: pointer;
    }
    .guide-map-popup-title {
      padding: 0 34px;
      font-family: 'Open Sans';
      font-style: normal;
      font-weight: 700;
      font-size: 20px;
      line-height: 28px;
      text-align: center;
      color: #000000;
    }
    .waypoint-list {
      margin-top: 12px;
      padding: 12px 0;
      background: #FFFFFF;
      box-shadow: 0px 2px 16px -3px #D8DFF5;
      border-radius: 10px;
      .waypoint-list-title {
        padding: 0 20px;
        font-family: 'Open Sans';
        font-style: normal;
        font-weight: 600;
        font-size: 12px;
        line-height: 16px;
        color: #000000;
      }
      .item-list {
        padding: 0 20px 2px;
        margin-top: 8px;
        max-height: 196px;
        overflow-y: scroll;
        overflow-y: overlay;
        &::-webkit-scrollbar {
          display: block;
          width: 4px;
          height: 0;
        }
        &::-webkit-scrollbar-thumb {
          width: 4px;
          height: 0;
          background-color: rgba(0, 0, 0, 0.3);
          &:hover {
            background-color: rgba(0, 0, 0, 0.5);
          }
        }
        .item {
          display: flex;
          &:nth-of-type(1) ~ .item {
            margin-top: 12px;
          }
          .waypoint-genre {
            flex-shrink: 0;
            position: relative;
            display: flex;
            align-items: center;
            justify-content: center;
            width: 20px;
            .ic-waypoint {
              font-size: 20px;
            }
            .waypoint-index {
              display: inline-block;
              width: 20px;
              height: 20px;
              border: 2px solid #000000;
              border-radius: 2px;
              font-family: 'Open Sans';
              font-style: normal;
              font-weight: 700;
              font-size: 14px;
              line-height: 16px;
              text-align: center;
              color: #000;
            }
            .dividing {
              position: absolute;
              top: 35px;
              left: 50%;
              transform: translateX(-50%);
              .point {
                margin-bottom: 3px;
                display: block;
                width: 3px;
                height: 3px;
                background: #000000;
                border-radius: 100%;
              }
            }
          }
          .input-container {
            flex: 1;
            margin-left: 12px;
            height: 40px;
          }
        }
      }
      .add-waypoint {
        padding: 10px 20px 0;
        display: inline-flex;
        align-items: center;
        user-select: none;
        cursor: pointer;
        font-family: 'Open Sans';
        font-style: normal;
        font-weight: 600;
        font-size: 12px;
        line-height: 22px;
        text-transform: uppercase;
        color: #4066D4;
        &.is-disabled {
          color: #B2B6C0;
          .add-icon {
            color: #B2B6C0;
          }
        }
        .add-icon {
          margin-right: 4px;
          font-size: 16px;
          color: #4066D4;
        }
      }
    }
    .footer {
      margin-top: 20px;
      display: flex;
      justify-content: center;
      button {
        user-select: none;
        outline: none;
        cursor: pointer;
        margin: 0;
        padding: 0 8px;
        width: 240px;
        height: 44px;
        border: 0;
        background: linear-gradient(180deg, #4066D4 0%, #1441C6 63.54%, #2453DA 100%);
        border-radius: 4px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        &:disabled {
          background: linear-gradient(180deg, #D4D4D4 0%, #C6C6C6 63.54%, #DADADA 100%);
          cursor: not-allowed;
        }
        &.is-loading {
          background: linear-gradient(180deg, #4066D4 0%, #1441C6 63.54%, #2453DA 100%) !important;
        }
        .ic-loading {
          margin-right: 4px;
          flex-shrink: 0;
          width: 18px;
          height: 18px;
          animation: rotate .8s linear infinite;
          @keyframes rotate {
            from {
              transform: rotate(0deg);
            }
            to {
              transform: rotate(360deg);
            }
          }
        }
        .text {
          font-family: 'Open Sans';
          font-style: normal;
          font-weight: 700;
          font-size: 14px;
          line-height: 22px;
          text-transform: uppercase;
          color: #FFFFFF;
        }
      }
    }
  }
</style>
